import { useRef, useState, type ChangeEvent, type SVGProps } from "react";
import {
  ACCEPT_ATTACHMENTS,
  useAttachments,
  type AttachmentTarget,
  type AttachmentView,
  type PendingUpload,
} from "../app/attachments";
import { useCollationTag, useT, type Translate } from "../i18n/index.js";
import { CloudOffIcon, PlusIcon } from "./icons";
import { MediaViewer } from "./MediaViewer";
import { cn } from "../lib/cn";
import { useMedia } from "../lib/media";

/**
 * The files hanging off one entity (SPEC §4.5, §4.6).
 *
 * A single horizontal row: pictures as thumbnails, anything else as a named
 * card, and an add button at the end. Every tile is the same fixed square,
 * including the ones that are still loading or still uploading, so nothing on
 * the screen below moves as images arrive — a strip that reflows under a
 * thumb mid-tap is worse than a slow one.
 *
 * Tapping a picture opens `MediaViewer` over the app; tapping anything else
 * opens it at full size in a new tab. The split is not tidiness. A PDF wants
 * pagination and a text layer, which the browser's own viewer does better than
 * anything worth writing here — and the media route serves it with `nosniff`
 * and a locked-down CSP. A photograph wants none of that and wants one thing
 * the in-app browser has no way to offer: the system share sheet, which is
 * where "Сохранить в фото" and "Скопировать" live.
 *
 * In an encrypted space none of that can happen straight from the URL: the
 * server holds an envelope of octets and the picture only exists once this
 * device has opened it. `useMedia` is what does that, and it is also what
 * decides *which* tile a file gets — the type is inside the envelope, so before
 * the file is open the row's `application/octet-stream` is all there is, and
 * every photograph would otherwise be filed as a document.
 */

const TILE = "size-[5.25rem] shrink-0 overflow-hidden rounded-row";

/**
 * A photograph in a conversation, which is not the same thing as a thumbnail.
 *
 * An 84px square is right on a wish or an event, where the file is an
 * attachment to something else and the something else is the point. In a chat
 * the photograph *is* the message, and at thumbnail size it was a stamp
 * somebody had to open to see. Height is fixed and width follows the picture:
 * the line of messages never reflows vertically as images arrive, and a
 * portrait photo is not cropped square.
 */
const TILE_WIDE =
  "h-[13.5rem] min-w-[8rem] max-w-full shrink-0 overflow-hidden rounded-row";

export interface AttachmentStripProps {
  spaceId: string | null;
  target: AttachmentTarget;
  /** Hide the add and remove controls — for a read-only rendering. */
  readOnly?: boolean;
  /**
   * Trade the 84px dashed tile for a line of text.
   *
   * The tile is right where attaching is the point of the screen. It is wrong
   * on a card that usually has no files at all: a wishes list came out with a
   * head-height empty box under every wish, which is a lot of weight for an
   * affordance almost nobody uses on almost every row. Compact renders `+ Файл`
   * instead, next to `+ Теги`, and only grows tiles once something is there.
   */
  compact?: boolean;
  /**
   * Draw pictures at chat size rather than thumbnail size.
   *
   * Only the conversation asks for this. Everywhere else a file hangs off
   * something — a wish, an event — and a large picture would push what it is
   * attached to off the screen.
   */
  wide?: boolean;
  className?: string;
}

/** A sheet of paper with a folded corner. Inline so nothing has to be fetched. */
function FileIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path d="M14 3H7.5A1.5 1.5 0 0 0 6 4.5v15A1.5 1.5 0 0 0 7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V7l-4-4Z" />
      <path d="M14 3v4h4" />
    </svg>
  );
}

/** Bytes in the units a person reads, in their own language. */
function formatBytes(bytes: number, t: Translate, tag: string): string {
  if (bytes < 1024) return t("attachments.sizeBytes", { size: bytes });
  if (bytes < 1024 * 1024) {
    return t("attachments.sizeKilobytes", { size: Math.round(bytes / 1024) });
  }
  // The decimal separator is the language's, so «1,5 МБ» is not "1,5 MB" in English.
  return t("attachments.sizeMegabytes", {
    size: (bytes / 1024 / 1024).toLocaleString(tag, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
  });
}

/* ------------------------------------------------------------------ tiles */

function ImageTile({
  file,
  href,
  mime,
  wide,
}: {
  file: AttachmentView;
  href: string;
  /** The real type, for the share sheet: the row's own is a guess. */
  mime: string;
  /** Chat size rather than thumbnail size — see `TILE_WIDE`. */
  wide: boolean;
}) {
  const t = useT();
  const [viewing, setViewing] = useState(false);
  const tileRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={tileRef}
        type="button"
        onClick={() => setViewing(true)}
        title={file.name}
        aria-label={t("attachments.open", { name: file.name })}
        className={cn(wide ? TILE_WIDE : TILE, "block bg-surface-alt")}
      >
        {/* Width and height are set as attributes as well as classes: the box is
            the right size before the bytes arrive, so the row does not jump. */}
        <img
          src={href}
          alt={file.name}
          width={wide ? 216 : 84}
          height={wide ? 216 : 84}
          loading="lazy"
          decoding="async"
          className={cn(
            "object-cover",
            // Wide keeps the height and lets the width be whatever the picture
            // is; the square crops, because a row of tiles has to line up.
            wide ? "h-full w-auto max-w-full" : "size-full",
          )}
        />
      </button>

      {viewing ? (
        <MediaViewer
          url={href}
          name={file.name}
          mime={mime}
          returnFocusTo={tileRef}
          onClose={() => setViewing(false)}
        />
      ) : null}
    </>
  );
}

function FileTile({
  file,
  href,
  opened,
}: {
  file: AttachmentView;
  href: string;
  /** True when `href` is a blob this device made, rather than the media route. */
  opened: boolean;
}) {
  const t = useT();
  const tag = useCollationTag();

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      // A blob URL has no name of its own — saving one gives a file called
      // `a1b2c3…`, which is not what a person attached. The media route's own
      // URL is left alone, so a PDF in a plaintext space still opens in the
      // browser's viewer rather than landing in the downloads folder.
      {...(opened ? { download: file.name } : {})}
      title={file.name}
      aria-label={t("attachments.open", { name: file.name })}
      className={cn(
        TILE,
        "flex flex-col items-center justify-center gap-1 bg-surface-alt px-2 text-center",
      )}
    >
      <FileIcon className="size-6 text-muted" />
      <span className="line-clamp-2 w-full text-[0.6875rem] leading-tight text-ink-soft">
        {file.name}
      </span>
      <span className="tabular text-[0.625rem] text-muted">
        {formatBytes(file.bytes, t, tag)}
      </span>
    </a>
  );
}

/** A file this device could not open: no key, another space's key, bad bytes. */
function BrokenTile({ file }: { file: AttachmentView }) {
  const t = useT();

  return (
    <div
      title={file.name}
      className={cn(
        TILE,
        "flex flex-col items-center justify-center gap-1 bg-surface-alt px-2 text-center",
      )}
    >
      <FileIcon className="size-6 text-muted/60" />
      <span className="line-clamp-2 text-[0.6875rem] leading-tight text-muted">
        {t("attachments.unreadable")}
      </span>
    </div>
  );
}

/**
 * One file, whichever kind it turns out to be.
 *
 * The hook is called here rather than in the strip because it is per file, and
 * it decides the tile: in an encrypted space the type only exists once the
 * envelope is open, so the choice between a thumbnail and a card cannot be made
 * from the row alone.
 */
function AttachmentTile({
  spaceId,
  file,
  wide = false,
}: {
  spaceId: string | null;
  file: AttachmentView;
  /** Show pictures at chat size. Documents keep the square either way. */
  wide?: boolean;
}) {
  const t = useT();
  const media = useMedia(spaceId, file.url);

  if (media.status === "loading" || !media.url) {
    return media.status === "failed" ? (
      <BrokenTile file={file} />
    ) : (
      /*
       * The same box as the finished tile, so the row has its height from the
       * first frame and nothing moves under a thumb when the file arrives.
       *
       * `wide` included, and that was the bug: a chat photograph waited as an
       * 84px square and became 216px tall on arrival, so every picture pushed
       * the conversation down by 132px as it loaded — which on opening a chat
       * meant landing well above the newest message, somewhere in the middle of
       * the day. In a sealed space nothing here knows yet whether the file is a
       * photograph, so the wide box is reserved for whatever it turns out to
       * be: documents in a conversation are the rare case, and a moment of
       * extra height is cheaper than a moving conversation.
       */
      <span
        aria-busy="true"
        aria-label={t("attachments.opening")}
        title={file.name}
        className={cn(wide ? TILE_WIDE : TILE, "block animate-pulse bg-surface-alt")}
      />
    );
  }

  // The row's type is the server's word for it, which in an encrypted space is
  // deliberately nothing; the opened envelope's is the real one.
  const isImage = media.mime ? media.mime.startsWith("image/") : file.isImage;
  const opened = media.url !== file.url;

  return isImage ? (
    <ImageTile
      file={file}
      href={media.url}
      mime={media.mime ?? file.mime}
      wide={wide}
    />
  ) : (
    <FileTile file={file} href={media.url} opened={opened} />
  );
}

/**
 * A file on its way up — or one that did not make it.
 *
 * The progress is the real fraction of bytes acknowledged by the browser, not a
 * spinner: on a phone's uplink a 10 MB photo takes long enough that "something
 * is happening" is not enough of an answer.
 */
function PendingTile({
  upload,
  onDismiss,
}: {
  upload: PendingUpload;
  onDismiss: () => void;
}) {
  const t = useT();

  if (upload.error) {
    return (
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t("attachments.dismissError", { error: upload.error })}
        className={cn(
          TILE,
          "flex flex-col items-center justify-center gap-1 bg-neg-soft px-2 text-center",
        )}
      >
        <span className="line-clamp-3 text-[0.6875rem] leading-tight text-neg">
          {upload.error}
        </span>
        <span className="text-[0.625rem] text-neg/80">{t("attachments.tapToDismiss")}</span>
      </button>
    );
  }

  /*
   * Waiting is its own state, not an upload stuck at nought.
   *
   * A queued file is the opposite of a failed one — the bytes are on disk and
   * will go by themselves — but drawn as 0% it looks exactly like an upload
   * that has given up, which is the reading that would send somebody hunting
   * for a problem that does not exist.
   */
  if (upload.queued) {
    return (
      <div
        aria-label={t("attachments.waitingForNetwork", { name: upload.name })}
        title={upload.name}
        className={cn(
          TILE,
          "flex flex-col items-center justify-center gap-1 bg-surface-alt px-2 text-center",
        )}
      >
        <CloudOffIcon className="size-5 text-muted" strokeWidth={1.8} />
        <span className="line-clamp-2 text-[0.625rem] leading-tight text-muted">
          {t("attachments.waitingShort")}
        </span>
      </div>
    );
  }

  const percent = Math.round(upload.progress * 100);

  return (
    <div
      aria-busy="true"
      aria-label={t("attachments.uploading", { name: upload.name, percent })}
      title={upload.name}
      className={cn(
        TILE,
        "relative flex flex-col items-center justify-center gap-1 bg-surface-alt px-2 text-center",
      )}
    >
      <span className="tabular text-[0.8125rem] font-medium text-ink-soft">{percent}%</span>
      <span className="line-clamp-1 w-full text-[0.625rem] text-muted">{upload.name}</span>
      <span className="absolute inset-x-0 bottom-0 h-1 bg-line-soft">
        <span
          className="block h-full bg-accent transition-[width] duration-200"
          style={{ width: `${percent}%` }}
        />
      </span>
    </div>
  );
}

/** Same square as everything else, so the row has its final height at once. */
function SkeletonTile() {
  return (
    <span
      aria-hidden="true"
      className={cn(TILE, "block animate-pulse bg-surface-alt")}
    />
  );
}

/* ------------------------------------------------------------------ strip */

export function AttachmentStrip({
  spaceId,
  target,
  readOnly = false,
  compact = false,
  wide = false,
  className,
}: AttachmentStripProps) {
  const { attachments, pending, loading, error, upload, remove, dismiss } = useAttachments(
    spaceId,
    target,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useT();

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Cleared before the uploads start, so picking the same file twice in a row
    // still fires a change event.
    event.target.value = "";
    for (const file of files) void upload(target, file);
  };

  const failed = pending.find((row) => row.error !== null);
  const alert = failed?.error ?? error;

  if (loading && attachments.length === 0 && pending.length === 0) {
    return (
      <div className={cn("flex gap-2 overflow-x-auto px-1 py-1", className)}>
        <SkeletonTile />
        <SkeletonTile />
      </div>
    );
  }

  /*
   * `pending` counts, and leaving it out was a silent failure.
   *
   * A read-only strip under a chat bubble is the only thing watching an upload
   * that has just started: same target, same key. Returning null on "no
   * attachments yet" hid the progress tile for every first file — and hid the
   * error tile with it, so a rejected upload vanished without a word.
   */
  if (readOnly && attachments.length === 0 && pending.length === 0) return null;

  const nothingYet = attachments.length === 0 && pending.length === 0;

  const picker = (
    <input
      ref={inputRef}
      type="file"
      multiple
      // Exactly what the server will accept, so the picker cannot offer
      // something that comes back refused.
      accept={ACCEPT_ATTACHMENTS}
      onChange={onPick}
      className="hidden"
    />
  );

  if (compact && !readOnly && nothingYet) {
    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex min-h-[var(--touch)] w-fit items-center gap-1.5 rounded-pill px-2 text-[0.8125rem] text-muted transition-colors hover:text-accent"
        >
          <PlusIcon className="size-[18px]" strokeWidth={1.9} />
          {t("attachments.addFile")}
        </button>
        {picker}
        {alert ? (
          <p role="alert" className="px-1 text-[0.8125rem] text-neg">
            {alert}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <ul className="flex list-none gap-2 overflow-x-auto px-1 py-1" role="list">
        {attachments.map((file) => (
          <li key={file.id} className="relative shrink-0">
            <AttachmentTile spaceId={spaceId} file={file} wide={wide} />
            {readOnly ? null : (
              <button
                type="button"
                onClick={() => void remove(file.id)}
                aria-label={t("attachments.remove", { name: file.name })}
                // A full touch target, with the visible circle in its corner:
                // the tap area clears 44px without covering the thumbnail.
                className="absolute top-0 right-0 grid size-[var(--touch)] place-items-start justify-end p-1"
              >
                <span className="grid size-6 place-items-center rounded-pill bg-surface text-muted shadow-soft transition-colors hover:text-neg">
                  <span aria-hidden="true" className="text-[0.875rem] leading-none">
                    ×
                  </span>
                </span>
              </button>
            )}
          </li>
        ))}

        {pending.map((row) => (
          <li key={row.id} className="shrink-0">
            <PendingTile upload={row} onDismiss={() => dismiss(row.id)} />
          </li>
        ))}

        {readOnly ? null : (
          <li className="shrink-0">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              aria-label={t("attachments.addFile")}
              className={cn(
                TILE,
                "flex flex-col items-center justify-center gap-1 border border-dashed border-line",
                "bg-surface text-muted transition-colors hover:bg-surface-alt hover:text-accent",
              )}
            >
              <PlusIcon className="size-5" />
              <span className="text-[0.6875rem]">{t("common.add")}</span>
            </button>
            {picker}
          </li>
        )}
      </ul>

      {alert ? (
        <p role="alert" className="px-1 text-[0.8125rem] text-neg">
          {alert}
        </p>
      ) : null}
    </div>
  );
}
